import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ReferenceLine
} from 'recharts';
import {
  Users, AlertTriangle, Award, Target, Clock, TrendingUp, Lightbulb, DollarSign,
  Save, Edit2, UserX, UserCheck, Zap, CalendarDays, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { printProductivityReport } from './productivity_print';

const ICON_MAP = {
  alert: AlertTriangle,
  'alert-triangle': AlertTriangle,
  award: Award,
  target: Target,
  clock: Clock,
  'trending-up': TrendingUp,
  users: Users,
};

const LEVEL_STYLES = {
  danger: 'bg-red-50 border-red-200 text-red-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  info: 'bg-blue-50 border-blue-200 text-blue-900',
};

const ICON_COLORS = {
  danger: 'text-red-600',
  warning: 'text-amber-600',
  success: 'text-emerald-600',
  info: 'text-blue-600',
};

// --- Daily Hours Breakdown Modal ---
function MechanicDailyBreakdownModal({ mechanic, month, year, monthName, expectedDailyHours = 7.6, onClose }) {
  if (!mechanic) return null;
  const daysInMonth = new Date(year, month, 0).getDate();
  const daily = mechanic.daily_hours || {};
  const expectedPerDay = expectedDailyHours;

  // Build rows for every day of the month
  const rows = [];
  let totalHours = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const cell = daily[iso];
    const hours = cell ? cell.hours : 0;
    const source = cell ? cell.source : null;
    const dt = new Date(year, month - 1, d);
    const dayName = dt.toLocaleDateString('en-AU', { weekday: 'short' });
    const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
    totalHours += hours;
    rows.push({ day: d, iso, dayName, isWeekend, hours, source });
  }
  const workingDays = rows.filter(r => !r.isWeekend).length;
  const expectedTotal = workingDays * expectedPerDay;
  const deficit = expectedTotal - totalHours;

  return (
    <Dialog open={!!mechanic} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="mechanic-daily-breakdown-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-600" />
              {mechanic.mechanic_name} — Daily Hours · {monthName} {year}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="mr-6"
              onClick={() => printProductivityReport({
                mode: 'mechanic',
                mechanic,
                month,
                year,
                monthName,
                expectedDaily: expectedDailyHours,
              })}
              data-testid="print-mechanic-modal-btn"
            >
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
          </DialogTitle>
          <DialogDescription>
            Per-day hours pulled from completed job cards (⚡) and manual entries (✎). Weekends are shaded.
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-2 mb-3 text-sm">
          <div className="p-2 bg-blue-50 border border-blue-200 rounded">
            <p className="text-[10px] uppercase text-blue-600">Expected</p>
            <p className="font-bold text-blue-900">{expectedTotal.toFixed(1)}h</p>
            <p className="text-[10px] text-blue-500">{workingDays} weekdays × {expectedPerDay}h</p>
          </div>
          <div className="p-2 bg-emerald-50 border border-emerald-200 rounded">
            <p className="text-[10px] uppercase text-emerald-600">Worked</p>
            <p className="font-bold text-emerald-900">{totalHours.toFixed(1)}h</p>
            <p className="text-[10px] text-emerald-500">{rows.filter(r => r.hours > 0).length} days</p>
          </div>
          <div className={`p-2 border rounded ${deficit > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
            <p className={`text-[10px] uppercase ${deficit > 0 ? 'text-rose-600' : 'text-slate-600'}`}>
              {deficit > 0 ? 'Deficit' : 'Surplus'}
            </p>
            <p className={`font-bold ${deficit > 0 ? 'text-rose-900' : 'text-slate-900'}`}>
              {deficit > 0 ? '−' : '+'}{Math.abs(deficit).toFixed(1)}h
            </p>
          </div>
          <div className="p-2 bg-amber-50 border border-amber-200 rounded">
            <p className="text-[10px] uppercase text-amber-600">Performance</p>
            <p className="font-bold text-amber-900">{mechanic.performance_pct.toFixed(0)}%</p>
            <p className="text-[10px] text-amber-500">of target</p>
          </div>
        </div>

        {/* Per-day grid */}
        <div className="max-h-[55vh] overflow-y-auto border rounded">
          <table className="min-w-full text-xs" data-testid="mechanic-daily-table">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Day</th>
                <th className="p-2 text-right">Hours</th>
                <th className="p-2 text-center">Source</th>
                <th className="p-2 text-right">Progress (vs 7.6h)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const pct = r.isWeekend ? null : Math.min(100, (r.hours / expectedPerDay) * 100);
                const barClass = pct === null ? '' : pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : pct > 0 ? 'bg-rose-500' : 'bg-slate-200';
                return (
                  <tr key={r.iso} className={`border-t ${r.isWeekend ? 'bg-slate-50 text-slate-400' : ''}`}>
                    <td className="p-2 tabular-nums">{r.day} {monthName.slice(0,3)}</td>
                    <td className="p-2">{r.dayName}</td>
                    <td className={`p-2 text-right tabular-nums font-medium ${r.hours === 0 && !r.isWeekend ? 'text-rose-600' : ''}`}>
                      {r.hours > 0 ? `${r.hours.toFixed(1)}h` : r.isWeekend ? '—' : '0h'}
                    </td>
                    <td className="p-2 text-center">
                      {r.source === 'manual' && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" title="Manually entered">
                          <Edit2 className="h-2.5 w-2.5" /> manual
                        </span>
                      )}
                      {r.source === 'auto' && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" title="From completed job cards">
                          <Zap className="h-2.5 w-2.5" /> job cards
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {pct !== null && (
                        <div className="inline-block w-24 h-2 bg-slate-100 rounded-full overflow-hidden align-middle">
                          <div className={`h-full ${barClass}`} style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductivityAnalysisCard({ month, year, BACKEND_URL, formatCurrency, MONTH_NAMES, readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [labourRate, setLabourRate] = useState(120);
  const [editingRate, setEditingRate] = useState(false);
  const [savingRate, setSavingRate] = useState(false);
  const [tempRate, setTempRate] = useState('120');
  const [analysisMonth, setAnalysisMonth] = useState(month);
  const [analysisYear, setAnalysisYear] = useState(year);
  const [selectedMechanic, setSelectedMechanic] = useState(null);

  useEffect(() => { setAnalysisMonth(month); }, [month]);
  useEffect(() => { setAnalysisYear(year); }, [year]);

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/super-admin/financials/productivity-analysis?month=${analysisMonth}&year=${analysisYear}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setLabourRate(d.labour_rate || 120);
        setTempRate(String(d.labour_rate || 120));
      }
    } catch (e) {
      console.error('Failed to fetch productivity analysis:', e);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, analysisMonth, analysisYear]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  const handleSaveRate = async () => {
    const rate = parseFloat(tempRate);
    if (isNaN(rate) || rate < 0 || rate > 1000) {
      toast.error('Labour rate must be between 0 and 1000');
      return;
    }
    setSavingRate(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/workshop-settings/labour-rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ labour_rate: rate })
      });
      if (res.ok) {
        toast.success('Labour rate saved');
        setEditingRate(false);
        fetchAnalysis();
      } else {
        toast.error('Failed to save labour rate');
      }
    } catch (e) {
      toast.error('Failed to save labour rate');
    } finally {
      setSavingRate(false);
    }
  };

  const updateExcludedMechanics = async (newExcludedIds) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/workshop-settings/excluded-mechanics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ excluded_mechanic_ids: newExcludedIds })
      });
      if (res.ok) {
        fetchAnalysis();
      } else {
        toast.error('Failed to update excluded mechanics');
      }
    } catch (e) {
      toast.error('Failed to update excluded mechanics');
    }
  };

  const handleExcludeMechanic = (mechanicId, mechanicName) => {
    if (!confirm(`Exclude "${mechanicName}" from productivity calculations? They won't be counted in expected hours or deficit. You can re-include them anytime.`)) return;
    const currentExcluded = (data?.excluded_mechanics || []).map(m => m.mechanic_id);
    updateExcludedMechanics([...currentExcluded, mechanicId]);
    toast.success(`${mechanicName} excluded from productivity`);
  };

  const handleIncludeMechanic = (mechanicId, mechanicName) => {
    const currentExcluded = (data?.excluded_mechanics || []).map(m => m.mechanic_id);
    updateExcludedMechanics(currentExcluded.filter(id => id !== mechanicId));
    toast.success(`${mechanicName} re-included in productivity`);
  };

  if (loading || !data) {
    return (
      <Card className="p-6" data-testid="productivity-card">
        <div className="flex items-center justify-center h-48">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      </Card>
    );
  }

  const chartData = (data.per_mechanic || []).map(p => ({
    name: p.mechanic_name.length > 12 ? p.mechanic_name.slice(0, 12) + '…' : p.mechanic_name,
    fullName: p.mechanic_name,
    actual: p.actual_hours,
    expected: p.expected_hours,
    pct: p.performance_pct,
    color: p.color === 'green' ? '#10b981' : p.color === 'yellow' ? '#f59e0b' : '#ef4444'
  }));

  const colorClass = (c) => c === 'green'
    ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
    : c === 'yellow'
      ? 'bg-amber-50 border-amber-300 text-amber-900'
      : 'bg-red-50 border-red-300 text-red-900';

  const dotClass = (c) => c === 'green' ? 'bg-emerald-500' : c === 'yellow' ? 'bg-amber-500' : 'bg-red-500';

  const isOnTarget = data.total_deficit_hours <= 0;

  return (
    <Card className="p-6" data-testid="productivity-card">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Mechanic Productivity Analysis
          </h3>
          <p className="text-sm text-slate-500">
            {MONTH_NAMES[analysisMonth - 1]} {analysisYear} — {data.working_days} working days × {data.expected_daily_hours}h target = {data.expected_per_mechanic}h per mechanic
          </p>
          <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
            <Zap className="h-3 w-3" />
            Hours auto-pulled from completed job cards. Override via the Monthly Hours Grid below.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={analysisMonth.toString()} onValueChange={(v) => setAnalysisMonth(parseInt(v))}>
            <SelectTrigger className="w-[130px]" data-testid="prod-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTH_NAMES.map((m, i) => (
                <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={analysisYear.toString()} onValueChange={(v) => setAnalysisYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => printProductivityReport({
              mode: 'workshop',
              data,
              month: analysisMonth,
              year: analysisYear,
              monthName: MONTH_NAMES[analysisMonth - 1],
              expectedDaily: data.expected_daily_hours,
            })}
            data-testid="print-workshop-btn"
            title="Print workshop productivity report"
          >
            <Printer className="h-4 w-4 mr-1" /> Print Workshop
          </Button>
        </div>
      </div>

      {/* Labour Rate */}
      <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg mb-6" data-testid="labour-rate-row">
        <DollarSign className="h-5 w-5 text-slate-600" />
        <div className="flex-1">
          <Label className="text-xs text-slate-600">
            {readOnly ? 'Labour rate' : 'Labour rate (used to estimate revenue loss)'}
          </Label>
          {editingRate && !readOnly ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm">$</span>
              <Input
                type="number"
                step="0.01"
                value={tempRate}
                onChange={(e) => setTempRate(e.target.value)}
                className="w-32"
                data-testid="labour-rate-input"
              />
              <span className="text-sm text-slate-500">/ hour</span>
              <Button size="sm" onClick={handleSaveRate} disabled={savingRate} data-testid="labour-rate-save-btn">
                <Save className="h-3 w-3 mr-1" /> {savingRate ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingRate(false); setTempRate(String(labourRate)); }}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-base font-semibold text-slate-900">${labourRate.toFixed(2)} / hour</p>
              {!readOnly && (
                <Button size="sm" variant="ghost" onClick={() => setEditingRate(true)} data-testid="labour-rate-edit-btn">
                  <Edit2 className="h-3 w-3 mr-1" /> Edit
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className={`grid grid-cols-2 ${readOnly ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-3 mb-6`}>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="kpi-expected">
          <p className="text-xs text-blue-700 uppercase tracking-wide">Expected Hours</p>
          <p className="text-2xl font-bold text-blue-900 mt-1">{data.total_expected_hours.toFixed(0)}h</p>
          <p className="text-[11px] text-blue-600 mt-1">{data.total_mechanics} mechanic(s) × {data.expected_per_mechanic}h</p>
        </div>
        <div className={`p-4 border rounded-lg ${data.overall_performance_pct >= 100 ? 'bg-emerald-50 border-emerald-200' : data.overall_performance_pct >= 85 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="kpi-actual">
          <p className="text-xs uppercase tracking-wide opacity-80">Actual Hours</p>
          <p className="text-2xl font-bold mt-1">{data.total_actual_hours.toFixed(0)}h</p>
          <p className="text-[11px] mt-1 opacity-80">{data.overall_performance_pct.toFixed(0)}% of target</p>
        </div>
        <div className={`p-4 border rounded-lg ${isOnTarget ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`} data-testid="kpi-deficit">
          <p className="text-xs uppercase tracking-wide opacity-80">Deficit Hours</p>
          <p className={`text-2xl font-bold mt-1 ${isOnTarget ? 'text-emerald-900' : 'text-orange-900'}`}>
            {isOnTarget ? '0h' : `${data.total_deficit_hours.toFixed(0)}h`}
          </p>
          <p className="text-[11px] mt-1 opacity-80">{isOnTarget ? 'On target!' : 'Below target'}</p>
        </div>
        {!readOnly && (
          <div className={`p-4 border rounded-lg ${isOnTarget ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="kpi-loss">
            <p className="text-xs uppercase tracking-wide opacity-80">Estimated Revenue Loss</p>
            <p className={`text-2xl font-bold mt-1 ${isOnTarget ? 'text-emerald-900' : 'text-red-900'}`}>
              {formatCurrency(data.estimated_revenue_loss)}
            </p>
            <p className="text-[11px] mt-1 opacity-80">@ ${labourRate}/hr</p>
          </div>
        )}
      </div>

      {/* Status Counts */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
        <span className="flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
          <span className="text-emerald-700 font-medium">{data.counts.exceeding} meeting/exceeding</span>
        </span>
        <span className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full">
          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
          <span className="text-amber-700 font-medium">{data.counts.on_target} at 85–99%</span>
        </span>
        <span className="flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-full">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          <span className="text-red-700 font-medium">{data.counts.underperforming} below 85%</span>
        </span>
      </div>

      {/* Per-Mechanic Bar Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Per-Mechanic Performance</h4>
        {chartData.length === 0 ? (
          <div className="h-32 flex items-center justify-center text-slate-500">
            No mechanics found in this workshop
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 36)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} domain={[0, Math.max(data.expected_per_mechanic, ...chartData.map(c => c.actual)) * 1.1]} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip
                formatter={(value, name, props) => [
                  `${value.toFixed(1)}h (${props.payload.pct}% of ${props.payload.expected}h target)`,
                  props.payload.fullName
                ]}
              />
              <ReferenceLine x={data.expected_per_mechanic} stroke="#64748b" strokeDasharray="4 4" label={{ value: 'Target', position: 'top', fill: '#64748b', fontSize: 11 }} />
              <Bar dataKey="actual" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-Mechanic Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {(data.per_mechanic || []).map((m) => (
          <div
            key={m.mechanic_id}
            className={`p-3 border rounded-lg ${colorClass(m.color)} relative group cursor-pointer hover:shadow-md hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition-all`}
            data-testid={`mechanic-${m.mechanic_id}`}
            role="button"
            tabIndex={0}
            onClick={() => setSelectedMechanic(m)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedMechanic(m); }}
            title="Click to see daily breakdown"
          >
            {!readOnly && (
              <button
                onClick={(e) => { e.stopPropagation(); handleExcludeMechanic(m.mechanic_id, m.mechanic_name); }}
                className="absolute top-2 right-2 p-1 rounded hover:bg-white/60 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Exclude this mechanic from productivity"
                data-testid={`exclude-${m.mechanic_id}`}
              >
                <UserX className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                printProductivityReport({
                  mode: 'mechanic',
                  mechanic: m,
                  month: analysisMonth,
                  year: analysisYear,
                  monthName: MONTH_NAMES[analysisMonth - 1],
                  expectedDaily: data.expected_daily_hours,
                });
              }}
              className={`absolute top-2 ${readOnly ? 'right-2' : 'right-9'} p-1 rounded hover:bg-white/60 opacity-0 group-hover:opacity-100 transition-opacity`}
              title="Print this mechanic's daily breakdown"
              data-testid={`print-${m.mechanic_id}`}
            >
              <Printer className="h-3.5 w-3.5" />
            </button>
            <div className="flex items-center justify-between mb-1 pr-6">
              <span className="font-semibold flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${dotClass(m.color)}`}></span>
                {m.mechanic_name}
              </span>
              <span className="text-xs font-bold">{m.performance_pct.toFixed(0)}%</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span>{m.actual_hours.toFixed(1)}h / {m.expected_hours}h</span>
              <span className="font-medium">
                {m.deficit_hours > 0 ? `−${m.deficit_hours.toFixed(1)}h` : `+${Math.abs(m.deficit_hours).toFixed(1)}h`}
              </span>
            </div>
            <div className="w-full bg-white/60 rounded-full h-1.5 mt-2 overflow-hidden">
              <div
                className={dotClass(m.color)}
                style={{ width: `${Math.min(100, m.performance_pct)}%`, height: '100%' }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-2 text-[11px] opacity-80">
              {m.auto_hours_from_jobs > 0 ? (
                <span className="flex items-center gap-1" title="Auto-pulled from completed job cards">
                  <Zap className="h-3 w-3" />
                  {m.auto_hours_from_jobs.toFixed(1)}h from job cards
                </span>
              ) : (
                <span className="opacity-60">No job-card hours yet</span>
              )}
              {!readOnly && m.estimated_loss > 0 && (
                <span>Loss: {formatCurrency(m.estimated_loss)}</span>
              )}
            </div>
            <div className="mt-1.5 text-[10px] text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              <CalendarDays className="h-3 w-3" /> click for daily breakdown
            </div>
          </div>
        ))}
      </div>

      {/* Excluded Mechanics */}
      {(data.excluded_mechanics || []).length > 0 && (
        <div className="mb-6 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="excluded-mechanics-section">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700">
            <UserX className="h-4 w-4" />
            Excluded from productivity ({data.excluded_mechanics.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {data.excluded_mechanics.map(m => (
              readOnly ? (
                <span
                  key={m.mechanic_id}
                  className="flex items-center gap-1 px-2.5 py-1 bg-white border border-slate-300 rounded-full text-xs"
                >
                  <UserX className="h-3 w-3 text-slate-500" />
                  <span>{m.mechanic_name}</span>
                </span>
              ) : (
                <button
                  key={m.mechanic_id}
                  onClick={() => handleIncludeMechanic(m.mechanic_id, m.mechanic_name)}
                  className="flex items-center gap-1 px-2.5 py-1 bg-white border border-slate-300 rounded-full text-xs hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                  title="Click to re-include"
                  data-testid={`include-${m.mechanic_id}`}
                >
                  <UserCheck className="h-3 w-3 text-emerald-600" />
                  <span>{m.mechanic_name}</span>
                </button>
              )
            ))}
          </div>
          {!readOnly && (
            <p className="text-[11px] text-slate-500 mt-2">
              Tip: hover any mechanic card above and click the X to exclude. Excluded mechanics don't count toward expected hours or deficit.
            </p>
          )}
        </div>
      )}

      {/* Insights & Recommendations */}
      {data.insights && data.insights.length > 0 && (() => {
        // Hide any insight that exposes dollar figures for read-only viewers.
        const visibleInsights = readOnly
          ? data.insights.filter(ins => !/\$/.test(`${ins.title} ${ins.message}`))
          : data.insights;
        if (visibleInsights.length === 0) return null;
        return (
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              {readOnly ? 'Insights & Recommendations' : 'Insights & Recommendations to Improve Financials'}
            </h4>
            <div className="space-y-2">
              {visibleInsights.map((ins, idx) => {
                const Icon = ICON_MAP[ins.icon] || Lightbulb;
                return (
                  <div
                    key={idx}
                    className={`p-3 border rounded-lg flex gap-3 ${LEVEL_STYLES[ins.level] || LEVEL_STYLES.info}`}
                    data-testid={`insight-${idx}`}
                  >
                    <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${ICON_COLORS[ins.level] || ICON_COLORS.info}`} />
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{ins.title}</p>
                      <p className="text-xs mt-0.5 opacity-90">{ins.message}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Daily Breakdown Modal */}
      <MechanicDailyBreakdownModal
        mechanic={selectedMechanic}
        month={analysisMonth}
        year={analysisYear}
        monthName={MONTH_NAMES[analysisMonth - 1]}
        expectedDailyHours={data.expected_daily_hours || 7.6}
        onClose={() => setSelectedMechanic(null)}
      />
    </Card>
  );
}
